// In-call view, Stage mode (mockup A): top bar, stage (screen share), participant strip,
// control dock, host toasts. Built on layout slots so Work mode (layer 5) re-arranges them.
import {
  LiveKitRoom,
  RoomAudioRenderer,
  StartAudio,
  VideoTrack,
  useIsSpeaking,
  useParticipants,
  useRoomContext,
  useConnectionQualityIndicator,
  useConnectionState,
  useTrackToggle,
  useTracks,
  type TrackReferenceOrPlaceholder,
} from '@livekit/components-react';
import { ConnectionQuality, ConnectionState, MediaDeviceFailure, RoomEvent, Track, type Participant, type ScreenShareCaptureOptions, type TrackPublishOptions } from 'livekit-client';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type Me, type Presentation, type Snapshot } from '../api';
import { FilesPanel, GuestsPanel, UploadRequestToasts } from '../components/CallPanels';
import { Brand, GuideButton } from '../components/Chrome';
import { micErrorText } from '../components/MicCheck';
import { t, T, tenant } from '../tenant';
import { guideOn, tip } from '../guide';

interface CallProps {
  meetingId: string;
  url: string;
  token: string;
  host: boolean;
  title: string;
  micDeviceId: string | null | undefined;
  /** The pre-join mic check's error name, if it failed. */
  micError?: string;
  camera: boolean;
  onEnded: (reason: string | null) => void;
  onLeft: () => void;
}

type ReportPhase = 'prejoin' | 'join' | 'toggle' | 'room';

/** Sends a media failure to the meeting record, so the host and agents can see why someone has no mic. */
function report(meetingId: string, phase: ReportPhase, source: string, err: unknown) {
  const e = err as { name?: string; message?: string } | undefined;
  void api(`/api/m/${meetingId}/report`, { body: { phase, source, name: e?.name || 'Error', message: e?.message ?? String(err ?? '') }, meetingId }).catch(() => {});
}

// LiveKit's join-time failure carries only a category; map it back to the getUserMedia name.
const FAILURE_NAME: Record<MediaDeviceFailure, string> = {
  [MediaDeviceFailure.PermissionDenied]: 'NotAllowedError',
  [MediaDeviceFailure.NotFound]: 'NotFoundError',
  [MediaDeviceFailure.DeviceInUse]: 'NotReadableError',
  [MediaDeviceFailure.Other]: 'Error',
};

export function Call(p: CallProps) {
  const [mediaError, setMediaError] = useState<string | null>(null);
  const fail = useCallback(
    (phase: ReportPhase, source: string, err: unknown) => {
      const name = (err as { name?: string })?.name;
      // Cancelling the screen picker is a choice, not a failure.
      if (source === 'screen_share' && name === 'NotAllowedError') return;
      report(p.meetingId, phase, source, err);
      if (source === 'screen_share') return setMediaError('Screen sharing could not start on this device.');
      const camera = source === 'camera' || source === 'videoinput';
      // The device-specific fix is the same for the camera; only the noun changes.
      setMediaError(camera ? micErrorText(err).replace(/microphone/g, 'camera').replace(/Microphone/g, 'Camera') : micErrorText(err));
    },
    [p.meetingId],
  );
  useEffect(() => {
    if (p.micError) report(p.meetingId, 'prejoin', 'microphone', { name: p.micError, message: 'mic check failed before joining' });
  }, [p.meetingId, p.micError]);
  return (
    <LiveKitRoom
      serverUrl={p.url}
      token={p.token}
      connect
      audio={p.micDeviceId === null ? false : p.micDeviceId && p.micDeviceId !== 'default' ? { deviceId: p.micDeviceId } : true}
      video={p.camera}
      options={{ adaptiveStream: true, dynacast: true }}
      onMediaDeviceFailure={(failure, kind) => fail('join', kind ?? 'audioinput', { name: FAILURE_NAME[failure ?? MediaDeviceFailure.Other], message: `join-time ${failure ?? 'failure'}` })}
      onError={(err) => report(p.meetingId, 'room', 'room', err)}
      onDisconnected={() => {
        /* the status poll decides between "ended" and "left" */
      }}
      className="call"
      data-lk-theme="none"
    >
      <CallBody {...p} mediaError={mediaError} onMediaError={fail} clearMediaError={() => setMediaError(null)} />
      <RoomAudioRenderer />
    </LiveKitRoom>
  );
}

interface BodyProps extends CallProps {
  mediaError: string | null;
  onMediaError: (phase: ReportPhase, source: string, err: unknown) => void;
  clearMediaError: () => void;
}

function initials(name: string): string {
  return (
    name
      .replace(/\(.*?\)/g, '')
      .split(/\s+/)
      .filter((w) => /[A-Za-z]/.test(w) && !/^(ir|en|pn|dr|ts|cik)\.?$/i.test(w))
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join('') || '?'
  );
}

function useElapsed(): string {
  const start = useRef(Date.now());
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const s = Math.floor((now - start.current) / 1000);
  return [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60].map((n) => String(n).padStart(2, '0')).join(':');
}

/** Tells the server the meeting is in use: local speech, or a screen share running. Throttled. */
function useActivityReporter(meetingId: string) {
  const room = useRoomContext();
  useEffect(() => {
    let last = 0;
    const report = () => {
      if (Date.now() - last < 60_000) return;
      last = Date.now();
      void api(`/api/m/${meetingId}/activity`, { body: {}, meetingId }).catch(() => {});
    };
    const onSpeakers = (speakers: Participant[]) => {
      if (speakers.some((s) => s.isLocal)) report();
    };
    room.on(RoomEvent.ActiveSpeakersChanged, onSpeakers);
    const t = setInterval(() => {
      if (room.localParticipant.isScreenShareEnabled) report();
    }, 30_000);
    return () => {
      room.off(RoomEvent.ActiveSpeakersChanged, onSpeakers);
      clearInterval(t);
    };
  }, [room, meetingId]);
}

/** Raised hands, oldest first, from the participant attributes the server sets (hand = raise time). */
function useHands(): { identity: string; name: string; at: number }[] {
  const room = useRoomContext();
  const [, bump] = useState(0);
  useEffect(() => {
    const update = () => bump((n) => n + 1);
    const events = [RoomEvent.ParticipantAttributesChanged, RoomEvent.ParticipantConnected, RoomEvent.ParticipantDisconnected, RoomEvent.Connected];
    events.forEach((e) => room.on(e, update));
    return () => events.forEach((e) => room.off(e, update));
  }, [room]);
  return [room.localParticipant, ...room.remoteParticipants.values()]
    .filter((x) => x.attributes?.hand)
    .map((x) => ({ identity: x.identity, name: x.name || x.identity, at: Number(x.attributes.hand) }))
    .sort((a, b) => a.at - b.at);
}

const DocStage = lazy(() => import('../components/DocStage'));

/** The file on the stage: room metadata from LiveKit (instant, and on join), the status poll as a fallback. */
function useStage(me: Me | null): Presentation | null {
  const room = useRoomContext();
  const read = () => {
    try {
      return room.metadata ? ((JSON.parse(room.metadata) as { presenting?: Presentation | null }).presenting ?? null) : undefined;
    } catch {
      return undefined;
    }
  };
  const [fromRoom, setFromRoom] = useState<Presentation | null | undefined>(read);
  useEffect(() => {
    const update = () => setFromRoom(read());
    room.on(RoomEvent.RoomMetadataChanged, update);
    room.on(RoomEvent.Connected, update);
    return () => {
      room.off(RoomEvent.RoomMetadataChanged, update);
      room.off(RoomEvent.Connected, update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);
  return fromRoom !== undefined ? fromRoom : (me?.presenting ?? null);
}

// Screen share: H.264 uses the GPU encoder on most PCs and Macs and decodes in hardware on phones (VP8,
// LiveKit's default, is software). Text stays sharp: keep resolution, drop frames when bandwidth is short.
// H.264 is decoded everywhere, so no backup codec is needed (LiveKit adds one only for VP9/AV1/H.265).
const SCREEN_CAPTURE: ScreenShareCaptureOptions = { contentHint: 'detail', audio: false };
const SCREEN_PUBLISH: TrackPublishOptions = { videoCodec: 'h264', degradationPreference: 'maintain-resolution' };

function CallBody(p: BodyProps) {
  const room = useRoomContext();
  const elapsed = useElapsed();
  const participants = useParticipants();
  const [me, setMe] = useState<Me | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [panel, setPanel] = useState<'files' | 'guests' | null>(null);
  const [refresh, setRefresh] = useState(0);
  useActivityReporter(p.meetingId);
  const connection = useConnectionState();
  const presenting = useStage(me);
  const hands = useHands();
  const handsOn = tenant.features.handRaise;
  // Optimistic for the guest's own button; the attribute from the server confirms it.
  const [myHand, setMyHand] = useState<boolean | null>(null);
  const handUp = myHand ?? hands.some((h) => h.identity === room.localParticipant.identity);
  useEffect(() => setMyHand(null), [hands.length]);
  const raise = (raised: boolean) => {
    setMyHand(raised);
    void api(`/api/m/${p.meetingId}/hand`, { body: { raised }, meetingId: p.meetingId }).catch(() => setMyHand(null));
  };
  const lower = (guestId: string) => void api(`/api/m/${p.meetingId}/hand`, { body: { raised: false, guestId } }).catch(() => {});

  // Status poll: idle warning, and ending for everyone when the meeting closes.
  useEffect(() => {
    let stopped = false;
    const poll = async () => {
      try {
        const m = await api<Me>(`/api/m/${p.meetingId}/me`, { meetingId: p.meetingId });
        if (stopped) return;
        setMe(m);
        if (m.status === 'ended' || m.state !== 'admitted') {
          await room.disconnect();
          p.onEnded(m.endReason);
        }
      } catch (err) {
        if (err instanceof ApiError && (err.status === 410 || err.status === 404)) {
          await room.disconnect();
          p.onEnded(null);
        }
      }
    };
    void poll();
    const t = setInterval(poll, 10_000);
    return () => {
      stopped = true;
      clearInterval(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.meetingId, room]);

  // Host: waiting room and roster, faster.
  useEffect(() => {
    if (!p.host) return;
    let stopped = false;
    const poll = async () => {
      try {
        const s = await api<Snapshot>(`/api/m/${p.meetingId}/admin`);
        if (!stopped) setSnapshot(s);
      } catch {
        /* the status poll handles an ended meeting */
      }
    };
    void poll();
    const t = setInterval(poll, 3000);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [p.host, p.meetingId, refresh]);

  // A knock whose page has closed is stale: no toast; it returns if that guest comes back.
  const waiting = snapshot?.guests.filter((g) => g.state === 'waiting' && !g.stale) ?? [];
  const decide = async (guestId: string, admit: boolean) => {
    await api(`/api/m/${p.meetingId}/${admit ? 'admit' : 'deny'}`, { body: { guestId } }).catch(() => {});
    setSnapshot((s) => (s ? { ...s, guests: s.guests.map((g) => (g.id === guestId ? { ...g, state: admit ? 'admitted' : 'denied' } : g)) } : s));
  };
  const [endError, setEndError] = useState<string | null>(null);
  // End must be confirmed by the server; on failure the host stays connected and sees why.
  const endForAll = async () => {
    setEndError(null);
    try {
      await api(`/api/m/${p.meetingId}/end`, { body: {} });
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 410)) {
        setEndError(`The meeting did not end: ${(err as Error).message}`);
        setConfirmEnd(false);
        return;
      }
    }
    await room.disconnect();
    p.onEnded('host');
  };
  const minutesLeft = me ? Math.max(0, Math.ceil((me.idleEndsAt - Date.now()) / 60_000)) : 0;

  return (
    <>
      <div className="stripe" />
      <header className="bar">
        <Brand />
        <span className="muted" aria-hidden="true">
          |
        </span>
        <strong>{p.title}</strong>
        <span className="pill dim">{elapsed}</span>
        <span className="pill live" data-testid="live-count">
          <span className="dot" />
          Live · {participants.length} in call
        </span>
        <span className="spacer" />
        {p.host && snapshot && (
          <>
            <label className="row small" style={{ color: 'var(--color-text-anchor)' }}>
              <input
                type="checkbox"
                checked={snapshot.waitingRoom}
                onChange={(e) => {
                  const on = e.target.checked;
                  setSnapshot({ ...snapshot, waitingRoom: on });
                  void api(`/api/m/${p.meetingId}/waiting-room`, { body: { on } });
                }}
              />
              Waiting room
            </label>
            <button className="btn sm" onClick={() => navigator.clipboard?.writeText(`${location.origin}/m/${p.meetingId}?k=${snapshot.link}`)} {...tip('Copies this call’s link to send to a pupil.')}>
              Copy link
            </button>
          </>
        )}
        {p.host && guideOn && <GuideButton tour="call" />}
      </header>
      {(connection === ConnectionState.Reconnecting || connection === ConnectionState.SignalReconnecting) && (
        <div className="banner warn" role="status" data-testid="reconnecting">
          Connection lost. Reconnecting… You stay in the {t.meeting}; this clears when you are back.
        </div>
      )}
      {confirmEnd && !endError && (
        <div className="banner warn" role="alert" data-testid="confirm-banner">
          Press <strong>Confirm end</strong> to end the {t.meeting} for everyone. The link will stop working.
        </div>
      )}
      {p.mediaError && (
        <div className="banner warn" role="alert" data-testid="media-error">
          {p.mediaError}
          <button className="btn sm" onClick={p.clearMediaError}>
            Dismiss
          </button>
        </div>
      )}
      {endError && (
        <div className="banner warn" role="alert">
          {endError}
          <button className="btn sm" onClick={() => setEndError(null)}>
            Dismiss
          </button>
        </div>
      )}
      {me?.idleWarning ? (
        <div className="banner warn" role="alert" data-testid="idle-warning">
          No activity for a while. This meeting ends in about {minutesLeft} min.
          <button
            className="btn sm"
            onClick={() => {
              void api(`/api/m/${p.meetingId}/keep`, { body: {}, meetingId: p.meetingId }).then(() => setMe({ ...me, idleWarning: false }));
            }}
          >
            Keep meeting
          </button>
        </div>
      ) : (
        <div />
      )}
      <main className="call-main">
        <Stage meetingId={p.meetingId} presenting={presenting} canControl={!!presenting && (p.host || presenting.by === room.localParticipant.identity)} />
        <aside className="strip" aria-label="Participants">
          {participants.map((pt) => (
            <Tile key={pt.identity} participant={pt} hand={hands.findIndex((h) => h.identity === pt.identity) + 1} />
          ))}
        </aside>
        {panel === 'files' && <FilesPanel meetingId={p.meetingId} host={p.host} onClose={() => setPanel(null)} onPresented={() => setPanel(null)} />}
        {panel === 'guests' && p.host && <GuestsPanel meetingId={p.meetingId} snapshot={snapshot} onChange={() => setRefresh((n) => n + 1)} onClose={() => setPanel(null)} />}
        {p.host && (waiting.length > 0 || (snapshot?.uploads.length ?? 0) > 0 || (handsOn && hands.length > 0)) && (
          <div className="toasts">
            {handsOn && hands.length > 0 && (
              <div className="toast hands" data-testid="hands">
                <strong>✋ Raised hands</strong>
                {hands.map((h, i) => (
                  <div key={h.identity} className="row" data-testid="hand-row">
                    <span style={{ flex: 1 }}>
                      {i + 1}. {h.name}
                    </span>
                    <button className="btn ghost sm" onClick={() => lower(h.identity.replace(/^g_/, ''))}>
                      Lower
                    </button>
                  </div>
                ))}
                {hands.length > 1 && (
                  <button className="btn sm" onClick={() => lower('all')}>
                    Lower all
                  </button>
                )}
              </div>
            )}
            <UploadRequestToasts meetingId={p.meetingId} snapshot={snapshot} onChange={() => setRefresh((n) => n + 1)} />
            {waiting.map((g) => (
              <div key={g.id} className="toast" data-testid="knock">
                <strong>{g.name} wants to join</strong>
                <div className="row">
                  <button className="btn primary sm" onClick={() => decide(g.id, true)}>
                    Admit
                  </button>
                  <button className="btn ghost sm" onClick={() => decide(g.id, false)}>
                    Deny
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <footer className="dock">
        <StartAudio label="Tap to turn on sound" className="btn primary" />
        <Toggle source={Track.Source.Microphone} label="Mic" short="MIC" hint="Microphone. Red means muted; tap to speak." onError={p.onMediaError} onStart={p.clearMediaError} />
        <Toggle source={Track.Source.Camera} label="Camera" short="CAM" hint="Turn your camera on or off." onError={p.onMediaError} onStart={p.clearMediaError} />
        <Toggle source={Track.Source.ScreenShare} label="Screen" short="SCR" hint="Share your screen: a worksheet or slides." onError={p.onMediaError} onStart={p.clearMediaError} captureOptions={SCREEN_CAPTURE} publishOptions={SCREEN_PUBLISH} />
        {handsOn && !p.host && (
          <div className="ctl-wrap">
            <button className={`ctl ${handUp ? 'hand-up' : ''}`} aria-pressed={handUp} onClick={() => raise(!handUp)} aria-label={handUp ? 'Lower hand' : 'Raise hand'} data-testid="hand-button" {...tip('Raise your hand to ask a question.')}>
              ✋
            </button>
            {handUp ? 'Lower' : 'Raise hand'}
          </div>
        )}
        <div className="ctl-wrap">
          <button className={`ctl ${panel === 'files' ? 'on' : ''}`} onClick={() => setPanel(panel === 'files' ? null : 'files')} aria-label="Files" data-testid="files-button" {...tip('Session files. Present a PDF or picture to everyone in the call.')}>
            FILES
          </button>
          Files
        </div>
        {p.host && (
          <div className="ctl-wrap">
            <button className={`ctl ${panel === 'guests' ? 'on' : ''}`} onClick={() => setPanel(panel === 'guests' ? null : 'guests')} aria-label="Guests" data-testid="guests-button" {...tip('Who is here, and anyone waiting to be let in.')}>
              PPL
            </button>
            {T.guests}
          </div>
        )}
        <div className="ctl-wrap">
          <button
            className="ctl"
            {...tip('Leave the call. It carries on for everyone else.')}
            onClick={async () => {
              await room.disconnect();
              p.onLeft();
            }}
          >
            LEAVE
          </button>
          Leave
        </div>
        {p.host && (
          <div className="ctl-wrap">
            {confirmEnd ? (
              <button className="ctl end" onClick={endForAll} data-testid="confirm-end">
                Confirm end
              </button>
            ) : (
              <button
                className="ctl end"
                {...tip('Ends the lesson for everyone. Tap again to confirm.')}
                onClick={() => {
                  setConfirmEnd(true);
                  // Disarm after 6 s so a stray first press cannot linger.
                  setTimeout(() => setConfirmEnd(false), 6000);
                }}
              >
                END
              </button>
            )}
            End for all
          </div>
        )}
      </footer>
    </>
  );
}

function Toggle({
  source,
  label,
  short,
  hint,
  onError,
  onStart,
  captureOptions,
  publishOptions,
}: {
  source: Track.Source.Microphone | Track.Source.Camera | Track.Source.ScreenShare;
  label: string;
  short: string;
  hint: string;
  onError: (phase: ReportPhase, source: string, err: unknown) => void;
  onStart: () => void;
  captureOptions?: ScreenShareCaptureOptions;
  publishOptions?: TrackPublishOptions;
}) {
  const { enabled, pending, toggle } = useTrackToggle({ source, captureOptions, publishOptions, onDeviceError: (err) => onError('toggle', source, err) });
  const unsupported = source === Track.Source.ScreenShare && !navigator.mediaDevices?.getDisplayMedia;
  if (unsupported) return null;
  return (
    <div className="ctl-wrap">
      <button
        className={`ctl ${enabled ? 'on' : source === Track.Source.Microphone ? 'off' : ''}`}
        aria-pressed={enabled}
        aria-label={`${label} ${enabled ? 'on' : 'off'}`}
        {...tip(hint)}
        disabled={pending}
        onClick={() => {
          if (!enabled) onStart();
          void toggle();
        }}
      >
        {short}
      </button>
      {label}
    </div>
  );
}

function Stage({ meetingId, presenting, canControl }: { meetingId: string; presenting: Presentation | null; canControl: boolean }) {
  const screens = useTracks([Track.Source.ScreenShare], { onlySubscribed: false }).filter((t) => t.publication);
  const current = screens.at(-1);
  // A presented file and a screen share can both be live; the file leads, the screen is one tap away.
  const [showScreen, setShowScreen] = useState(false);
  useEffect(() => setShowScreen(false), [presenting?.versionId]);
  const doc = presenting && !(showScreen && current) ? presenting : null;
  return (
    <section className="stage" aria-label="Stage">
      <div className="stage-head">
        {doc ? (
          <>
            <span className="pill dim">File</span> <span data-testid="stage-title">{doc.byName} is presenting {doc.name}</span>
          </>
        ) : current ? (
          <>
            <span className="pill dim">Screen</span> {current.participant.name || current.participant.identity} is sharing
          </>
        ) : (
          <span style={{ opacity: 0.7 }}>Stage</span>
        )}
        <span className="spacer" />
        {presenting && current && (
          <button className="btn sm" onClick={() => setShowScreen(!showScreen)} data-testid="stage-switch">
            {doc ? 'Show screen' : 'Show file'}
          </button>
        )}
      </div>
      <div className="stage-body">
        {doc ? (
          <Suspense fallback={<div className="stage-empty">Opening {doc.name}…</div>}>
            <DocStage meetingId={meetingId} presenting={doc} canControl={canControl} />
          </Suspense>
        ) : current ? (
          <VideoTrack trackRef={current as TrackReferenceOrPlaceholder & { publication: NonNullable<TrackReferenceOrPlaceholder['publication']> }} />
        ) : (
          <div className="stage-empty">No one is sharing. Press SCR to share your screen, or present a file from FILES.</div>
        )}
      </div>
    </section>
  );
}

function Tile({ participant, hand }: { participant: Participant; hand: number }) {
  const speaking = useIsSpeaking(participant);
  // LiveKit rates each connection; "lost" shows well before a dropped pupil leaves the list (about 20 s).
  const { quality } = useConnectionQualityIndicator({ participant });
  const cams = useTracks([Track.Source.Camera]).filter((t) => t.participant.identity === participant.identity && t.publication && !t.publication.isMuted);
  const cam = cams[0];
  const name = participant.name || participant.identity;
  const micOn = participant.isMicrophoneEnabled;
  return (
    <div className={`tile${hand ? ' hand' : ''}`} data-testid="tile">
      {hand > 0 && (
        <span className="hand-badge" data-testid="hand-badge" aria-label={`hand raised, number ${hand}`}>
          ✋ {hand}
        </span>
      )}
      {cam ? (
        <VideoTrack trackRef={cam as TrackReferenceOrPlaceholder & { publication: NonNullable<TrackReferenceOrPlaceholder['publication']> }} />
      ) : (
        <span className={`avatar ${speaking ? 'speaking' : ''}`}>
          {initials(name)}
          {!micOn && <span className="mic-off" aria-label="microphone off" />}
        </span>
      )}
      <span className="tile-name">
        {name}
        {participant.isLocal ? ' (you)' : ''} {participant.identity === 'host' && <span className="tag">host</span>}
      </span>
      {(quality === ConnectionQuality.Lost || quality === ConnectionQuality.Poor) && (
        <span className={`tag net ${quality === ConnectionQuality.Lost ? 'lost' : 'poor'}`} data-testid="tile-connection">
          {quality === ConnectionQuality.Lost ? 'Connection lost' : 'Weak connection'}
        </span>
      )}
    </div>
  );
}
